import { Star, X, ChevronUp, ChevronDown } from "lucide-react";
import TeamLogoImage from "./TeamLogoImage";

/**
 * The current user's big board. Teams arrive in the order they were starred on
 * the Scouting page, which is the order they were read about rather than the
 * order they are wanted, so the rows can be reordered here. Drafted teams drop
 * off on their own.
 *
 * Tapping a team opens the same drawer the Scouting page opens, so a board
 * built days ago can be re-read before it is reordered.
 *
 * Reordering is chevrons rather than drag and drop. This panel sits inside a
 * scrolling column on a phone, where a long-press drag fights the scroll, and
 * the draft order lists elsewhere already move this way.
 */
export default function BigBoardPanel({ board, teams, limit, onSelect, onMove, onRemove }) {
  const byId = new Map(teams.map((t) => [t.id, t]));

  return (
    <div className="bg-surface-card rounded-2xl p-4 border border-hairline shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold flex items-center gap-2 text-ink">
          <Star size={16} className="text-brand" fill="var(--color-brand)" />
          My Big Board
        </h2>
        <span className="text-ink-faint text-sm">{board.length}/{limit}</span>
      </div>

      {board.length === 0 ? (
        <p className="text-ink-faint text-sm">
          No teams yet. Star teams on the Scouting page to build your board.
        </p>
      ) : (
        <>
        {onMove && board.length > 1 && (
          <p className="text-ink-faint text-xs mb-2">
            Use the arrows to rank your board. 1 is the team you want most.
          </p>
        )}
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {board.map((teamId, i) => {
            const team = byId.get(teamId);
            return (
              <div key={teamId} className="flex items-center gap-2 bg-surface-subtle rounded-lg p-2 border border-hairline">
                <span className="text-ink-faint text-xs font-semibold w-4 flex-shrink-0">{i + 1}</span>
                {/* Only the logo and name open the drawer. The chevrons and
                    the remove button sit in the same row, and a whole-row
                    target would swallow taps meant for them. */}
                <button
                  type="button"
                  onClick={onSelect && team?.school ? () => onSelect(team.school) : undefined}
                  disabled={!onSelect || !team?.school}
                  className="flex items-center gap-2 flex-1 min-w-0 text-left rounded enabled:hover:opacity-70 transition-opacity disabled:cursor-default"
                >
                  <TeamLogoImage teamName={team?.school} primaryColor={team?.color} size={20} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium text-sm text-ink">{team?.school ?? teamId}</div>
                    <div className="text-ink-faint text-xs truncate">{team?.conference}</div>
                  </div>
                </button>
                {onMove && board.length > 1 && (
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => onMove(teamId, -1)}
                      disabled={i === 0}
                      className="p-1 text-ink-faint hover:text-brand hover:bg-surface-subtle rounded disabled:opacity-25 disabled:hover:text-ink-faint transition-colors"
                      title={`Move ${team?.school ?? teamId} up`}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      onClick={() => onMove(teamId, 1)}
                      disabled={i === board.length - 1}
                      className="p-1 text-ink-faint hover:text-brand hover:bg-surface-subtle rounded disabled:opacity-25 disabled:hover:text-ink-faint transition-colors"
                      title={`Move ${team?.school ?? teamId} down`}
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                )}
                {onRemove && (
                  <button
                    onClick={() => onRemove(teamId)}
                    className="p-1 text-ink-faint hover:text-danger-text transition-colors flex-shrink-0"
                    title="Remove from big board"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}
